import { createAsyncThunk } from "@reduxjs/toolkit";
import { log, signalrConnection } from "@/helpers";
import { fileScanCompleted as feedbackFormFileScanCompleted } from "@/features/shared/feedbackFormSlice";
import { dynamicApiSlice } from "@/app/dynamicApi";
import {
  appendSystemThought,
  updateAdditionalInformationBatch,
} from "@/features/workflow/workflowSlice";
import i18n from "@/i18n";
import type { SystemThought } from "@/types";
import type { SystemThought as WorkflowStepSystemThought } from "@/types/SystemThought";
import { workflowStepInstanceSystemThoughtsApi } from "@/app/workflows/workflowStepInstanceSystemThoughtsApi";
import type { AppDispatch, RootState } from "@/app/store";
import {
  connection,
  connecting,
  startPromise,
  setConnection,
  setConnecting,
  setStartPromise,
} from "./shared";
import {
  connectionIdUpdated,
  roomAccessDenied,
  roomJoined,
} from "../signalrSlice";
import { toast } from "@bdo/cornerstone-ui";
import { fileScanComplete } from "@/features/new-workflow";

export const startSignalRConnection = createAsyncThunk<
  { hub: string },
  string,
  { rejectValue: string; dispatch: AppDispatch; state: RootState }
>("signalr/start", async (hub, { rejectWithValue, dispatch }) => {
  if (connection && !connecting) {
    log("startSignalRConnection", "reuse existing live connection", { hub });
    return { hub };
  }

  // If a start is already in progress, await it instead of returning early
  if (connecting && startPromise) {
    log("startSignalRConnection", "awaiting existing startPromise", { hub });
    try {
      await startPromise;
      if (connection) return { hub };
      return rejectWithValue("Connection failed during initialization");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      return rejectWithValue(message);
    }
  }

  try {
    setConnecting(true);
    const promise = (async () => {
      log("startSignalRConnection", "creating connection", { hub });
      const conn = await signalrConnection({ hub });
      setConnection(conn);

      // Remove any existing listeners before registering new ones to prevent duplicates on reconnect
      conn.off("RoomAccessDenied");
      conn.on("RoomAccessDenied", (roomId: string) => {
        log("RoomAccessDenied", "access denied to room", { roomId });
        dispatch(roomAccessDenied(roomId));
      });
      conn.off("RoomJoined");
      conn.on("RoomJoined", (roomId: string) => {
        log("RoomJoined", "joined room successfully", { roomId });
        dispatch(roomJoined(roomId));
      });
      conn.onclose(async (error) => {
        log("onclose", "connection closed", { error });
        setConnection(null);
        // Lazy import to avoid circular dependency
        const { stopSignalRConnection } = await import("./index");
        dispatch(stopSignalRConnection());
      });
      conn.off("FileMetaDataScanComplete");
      conn.on(
        "FileMetaDataScanComplete",
        (message: {
          fileId: string;
          fileName: string;
          isMalicious: boolean;
        }) => {
          log("FileMetaDataScanComplete", "file scan completed", { message });
          dispatch(fileScanComplete(message));
          // Can we refactor this to only use fileScanSlice?
          dispatch(feedbackFormFileScanCompleted(message));

          const notificationType = message.isMalicious ? "warning" : "success";
          const notificationDurationByType = {
            success: 3000,
            warning: 5000,
          } as const;

          toast[notificationType]({
            title: i18n.t(
              `feedback.notification.virusScan.${notificationType}.title`,
            ),
            message: i18n.t(
              `feedback.notification.virusScan.${notificationType}.message`,
              { fileName: message.fileName },
            ),
            duration: notificationDurationByType[notificationType],
          });
        },
      );
      conn.off("WorkflowInstanceUpdated");
      conn.on(
        "WorkflowInstanceUpdated",
        (message: { workflowInstanceId: string }) => {
          log("WorkflowInstanceUpdated", "workflow instance updated", {
            message,
          });
          dispatch(
            dynamicApiSlice.util.invalidateTags([
              { type: "WorkflowInstance", id: message.workflowInstanceId },
            ]),
          );
        },
      );
      conn.off("AdditionalInformationUpdated");
      conn.on(
        "AdditionalInformationUpdated",
        (message: { byRequestId: Record<string, Record<string, unknown>> }) => {
          log(
            "AdditionalInformationUpdated",
            "additional information updated",
            { message },
          );
          dispatch(
            updateAdditionalInformationBatch(message.byRequestId as never),
          );
        },
      );
      conn.off("WorkflowStepInstanceThoughtsUpdated");
      conn.on(
        "WorkflowStepInstanceThoughtsUpdated",
        (message: {
          workflowInstanceId: string;
          workflowStepInstanceId: string;
          title?: string;
          description?: string;
        }) => {
          log(
            "WorkflowStepInstanceThoughtsUpdated",
            "workflow step instance thoughts updated",
            { message },
          );

          const title = message.title?.trim() ?? "System Thought";
          const description = message.description?.trim();

            if (message.title && message.description) {
            const thought: SystemThought = {
              id: crypto.randomUUID(), // look into removing the id altogether and then use the description as key. We'll need to add a duplicate check in the reducer.
              title: message.title,
              description: message.description,
            };

            dispatch(
              workflowStepInstanceSystemThoughtsApi.util.updateQueryData(
                "getWorkflowStepInstanceThoughts",
                {
                  workflowInstanceId: message.workflowInstanceId,
                  workflowStepInstanceId: message.workflowStepInstanceId,
                },
                (draft) => {
                  const lastThought = draft.at(-1) ?? null;

                  if (lastThought?.description === description) {
                    return;
                  }

                  const cacheThought: WorkflowStepSystemThought = {
                    id: crypto.randomUUID(),
                    title,
                    description: description ?? "",
                    createdOn: new Date().toISOString(),
                    workflowStepInstanceId: message.workflowStepInstanceId,
                  };

                  draft.push(cacheThought);
                },
              ) as never,
            );

            dispatch(
              appendSystemThought({
                stepInstanceId: message.workflowStepInstanceId,
                thought,
              }),
            );
          }
        },
      );

      await conn.start();
      dispatch(connectionIdUpdated(conn.connectionId || ""));
      log("startSignalRConnection", "connection started", {
        hub,
        state: connection?.state,
        connectionId: connection?.connectionId,
      });
    })();

    setStartPromise(promise);
    await promise;
    return { hub };
  } catch (e: unknown) {
    setConnection(null);
    const message = e instanceof Error ? e.message : String(e);
    log("startSignalRConnection", "ERROR", { hub, message });
    return rejectWithValue(message || "Failed to start SignalR connection");
  } finally {
    setConnecting(false);
    setStartPromise(null);
    log("startSignalRConnection", "finished", { hub });
  }
});
